import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Progress } from '../components/ui/progress';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Clock, User, Calendar, FileText, AlertTriangle, MessageSquare, Send, UserCheck, Eye, RotateCcw, OctagonX, Pencil, Check } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';

const statusConfig = {
  pending: { label: 'Pending', color: 'bg-red-100 text-red-800' },
  in_progress: { label: 'In Progress', color: 'bg-amber-100 text-amber-800' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-800' },
  on_hold: { label: 'On Hold', color: 'bg-slate-100 text-slate-800' },
  overdue: { label: 'Overdue', color: 'bg-red-200 text-red-900' },
  pending_with_client: { label: 'Pending with Client', color: 'bg-orange-100 text-orange-800' },
  pending_with_member: { label: 'Pending with Member', color: 'bg-purple-100 text-purple-800' },
  on_hold_client: { label: 'On Hold - Client', color: 'bg-slate-100 text-slate-800' },
  pending_review: { label: 'Pending Review', color: 'bg-cyan-100 text-cyan-800' },
};

const getDeadlineStatus = (deadline) => {
  const now = new Date();
  const deadlineDate = new Date(deadline);
  const totalTime = deadlineDate - now;
  const daysRemaining = Math.ceil(totalTime / (1000 * 60 * 60 * 24));
  const hoursRemaining = Math.ceil(totalTime / (1000 * 60 * 60));
  let status = 'safe', color = 'bg-green-600', textColor = 'text-green-700', bgColor = 'bg-green-50', progressValue = 100;
  if (totalTime < 0) { status = 'overdue'; color = 'bg-red-600'; textColor = 'text-red-700'; bgColor = 'bg-red-50'; progressValue = 0; }
  else if (daysRemaining <= 1) { status = 'critical'; color = 'bg-red-600'; textColor = 'text-red-700'; bgColor = 'bg-red-50'; progressValue = 20; }
  else if (daysRemaining <= 3) { status = 'urgent'; color = 'bg-amber-600'; textColor = 'text-amber-700'; bgColor = 'bg-amber-50'; progressValue = 40; }
  else if (daysRemaining <= 7) { status = 'warning'; color = 'bg-yellow-500'; textColor = 'text-yellow-700'; bgColor = 'bg-yellow-50'; progressValue = 60; }
  return { status, color, textColor, bgColor, progressValue, daysRemaining, hoursRemaining, isOverdue: totalTime < 0 };
};

export default function TaskDetail() {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState(null);
  const [client, setClient] = useState(null);
  const [assignee, setAssignee] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isTimeLogDialogOpen, setIsTimeLogDialogOpen] = useState(false);
  const [timeLogData, setTimeLogData] = useState({ hours: '', description: '' });
  const [isEditMode, setIsEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  useEffect(() => { fetchTask(); }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { const i = setInterval(fetchTask, 10000); return () => clearInterval(i); }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTask = async () => {
    try {
      const [taskRes, commentsRes] = await Promise.all([
        api.get(`/tasks/${taskId}`),
        api.get(`/tasks/${taskId}/comments`)
      ]);
      setTask(taskRes.data);
      setComments(commentsRes.data);
      setEditData({
        title: taskRes.data.title, description: taskRes.data.description,
        status: taskRes.data.status, deadline: new Date(taskRes.data.deadline).toISOString().slice(0, 16),
        estimate_hours: taskRes.data.estimate_hours ?? '',
      });
      const [clientRes, usersRes] = await Promise.all([
        api.get(`/clients/${taskRes.data.client_id}`).catch(() => ({ data: null })),
        api.get('/users')
      ]);
      setClient(clientRes.data);
      setAllUsers(usersRes.data);
      setAssignee(usersRes.data.find(u => u.id === taskRes.data.assignee_id));
    } catch (error) {
      toast.error('Failed to load task details');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTimeLog = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/tasks/${taskId}/time-log`, timeLogData);
      toast.success('Time log added');
      setIsTimeLogDialogOpen(false);
      setTimeLogData({ hours: '', description: '' });
      fetchTask();
    } catch (error) { toast.error('Failed to add time log'); }
  };

  const handleUpdateTask = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...editData,
        estimate_hours: editData.estimate_hours !== '' && editData.estimate_hours != null
          ? parseFloat(editData.estimate_hours)
          : null,
      };
      await api.put(`/tasks/${taskId}`, payload);
      toast.success('Task updated');
      setIsEditMode(false);
      fetchTask();
    } catch (error) { toast.error('Failed to update task'); }
  };

  const handleStopRecurrence = async () => {
    if (!window.confirm(`Stop recurrence on "${task.title}"?\n\nThe current task stays open. No NEW instances will be created automatically when this one is completed.`)) return;
    try {
      const res = await api.put(`/tasks/${taskId}/stop-recurrence`);
      toast.success(`Recurrence stopped (${res.data.tasks_updated} task${res.data.tasks_updated === 1 ? '' : 's'} updated)`);
      fetchTask();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to stop recurrence'); }
  };

  const handleRestartRecurrence = async () => {
    if (!window.confirm(`Restart recurrence on "${task.title}"?\n\nA new ${task.recurrence_type} instance will be auto-created every time this task is completed.`)) return;
    try {
      const res = await api.put(`/tasks/${taskId}/restart-recurrence`);
      toast.success(`Recurrence restarted (${res.data.tasks_updated} task${res.data.tasks_updated === 1 ? '' : 's'} updated)`);
      fetchTask();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to restart recurrence'); }
  };



  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    setSubmittingComment(true);
    try {
      await api.post(`/tasks/${taskId}/comments`, { text: newComment.trim() });
      setNewComment('');
      const res = await api.get(`/tasks/${taskId}/comments`);
      setComments(res.data);
      toast.success('Comment added');
    } catch (error) {
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatCommentDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ' at ' + date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
  };

  if (loading) return <div className="p-8">Loading...</div>;
  if (!task) return <div className="p-8">Task not found</div>;

  const status = statusConfig[task.status] || { label: task.status, color: 'bg-slate-100 text-slate-800' };
  const totalHours = task.time_logs?.reduce((sum, log) => sum + log.hours, 0) || 0;
  const deadlineInfo = getDeadlineStatus(task.deadline);

  return (
    <div className="p-8 min-h-full" style={{
        background: 'linear-gradient(135deg, rgba(91, 111, 214, 0.03) 0%, rgba(16, 185, 129, 0.03) 100%)'
      }}>
        <Button variant="ghost" onClick={() => navigate('/tasks')} className="mb-6 hover:bg-slate-100" data-testid="back-to-tasks-button">
          <ArrowLeft className="h-5 w-5 mr-2" /> Back to Tasks
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            {/* Deadline Card */}
            <Card className={`border-slate-200 shadow-sm ${deadlineInfo.bgColor} border-2`} data-testid="deadline-status-card">
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Clock className={`h-5 w-5 ${deadlineInfo.textColor}`} />
                      <span className="font-semibold text-slate-900">Deadline Status</span>
                    </div>
                    <span className={`text-lg font-bold ${deadlineInfo.textColor} flex items-center gap-2`}>
                      {deadlineInfo.isOverdue ? (<><AlertTriangle className="h-5 w-5" />OVERDUE</>) :
                        deadlineInfo.daysRemaining === 0 ? `${deadlineInfo.hoursRemaining} hours remaining` :
                        `${deadlineInfo.daysRemaining} days remaining`}
                    </span>
                  </div>
                  <div className="relative">
                    <Progress value={deadlineInfo.progressValue} className="h-3" />
                    <div className={`absolute top-0 left-0 h-3 ${deadlineInfo.color} rounded-full transition-all`}
                      style={{ width: `${deadlineInfo.progressValue}%` }} />
                  </div>
                  <div className="flex items-center justify-between text-sm text-slate-600">
                    <span>Started: {new Date(task.created_at).toLocaleDateString()}</span>
                    <span>Due: {new Date(task.deadline).toLocaleDateString()} at {new Date(task.deadline).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Task Detail Card */}
            <Card className="border-slate-200 shadow-sm" data-testid="task-detail-card">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    {isEditMode ? (
                      <Input value={editData.title} onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                        className="text-2xl font-bold mb-2" data-testid="edit-task-title" />
                    ) : (
                      <CardTitle className="text-3xl font-bold tracking-tight mb-2">{task.title}</CardTitle>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${status.color}`}>{status.label}</span>
                      {task.recurrence_type && task.is_recurring && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700 ring-1 ring-violet-200" data-testid="recurring-active-pill">
                          <RotateCcw className="h-3 w-3" /> Recurring · {task.recurrence_type}
                        </span>
                      )}
                      {task.recurrence_type && !task.is_recurring && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 ring-1 ring-slate-200" data-testid="recurring-stopped-pill" title="Recurrence stopped — no new instances will be created on completion">
                          <OctagonX className="h-3 w-3" /> Recurrence stopped · {task.recurrence_type}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2 flex-wrap">
                    {/* Recurrence controls */}
                    {task.recurrence_type && (
                      task.is_recurring ? (
                        ['super_admin', 'admin'].includes(currentUser.role) || task.assignee_id === currentUser.id || task.created_by === currentUser.id
                      ) && (
                        <Button onClick={handleStopRecurrence} variant="outline" size="sm" className="text-amber-700 border-amber-300 hover:bg-amber-50" data-testid="stop-recurrence-btn">
                          <OctagonX className="h-3.5 w-3.5 mr-1.5" /> Stop recurrence
                        </Button>
                      ) : currentUser.role === 'super_admin' ? (
                        <Button onClick={handleRestartRecurrence} variant="outline" size="sm" className="text-emerald-700 border-emerald-300 hover:bg-emerald-50" data-testid="restart-recurrence-btn">
                          <RotateCcw className="h-3.5 w-3.5 mr-1.5" /> Restart recurrence
                        </Button>
                      ) : null
                    )}
                    {(() => {
                      const isMember = currentUser.role === 'member';
                      const isOwnTask = task.created_by === currentUser.id;
                      const canEdit = !isMember || isOwnTask;
                      if (!canEdit) return null;
                      return isEditMode ? (
                        <>
                          <Button onClick={handleUpdateTask} className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="save-task-button">Save</Button>
                          <Button variant="outline" onClick={() => setIsEditMode(false)}>Cancel</Button>
                        </>
                      ) : (
                        <Button onClick={() => setIsEditMode(true)} variant="outline" data-testid="edit-task-button">Edit</Button>
                      );
                    })()}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <Label className="text-sm font-medium text-slate-600">Description</Label>
                  {isEditMode ? (
                    <Textarea value={editData.description} onChange={(e) => setEditData({ ...editData, description: e.target.value })}
                      rows={4} className="mt-2" data-testid="edit-task-description" />
                  ) : (
                    <p className="mt-2 text-slate-900">{task.description}</p>
                  )}
                </div>
                {isEditMode && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Status</Label>
                      <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                        <SelectTrigger data-testid="edit-task-status"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(statusConfig).filter(([k]) => k !== 'overdue').map(([k, v]) => (
                            <SelectItem key={k} value={k}>{v.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Deadline</Label>
                      <Input type="datetime-local" value={editData.deadline}
                        onChange={(e) => setEditData({ ...editData, deadline: e.target.value })} data-testid="edit-task-deadline" />
                    </div>
                    <div>
                      <Label>Estimate (hours)</Label>
                      <Input type="number" min="0" step="0.25" placeholder="e.g. 2.5"
                        value={editData.estimate_hours}
                        onChange={(e) => setEditData({ ...editData, estimate_hours: e.target.value })}
                        data-testid="edit-task-estimate" />
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-[#4338CA]/10 flex items-center justify-center"><FileText className="h-5 w-5 text-[#4338CA]" /></div>
                    <div><p className="text-sm text-slate-600">Service Type</p><p className="font-medium text-slate-900">{task.service_name}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-[#059669]/10 flex items-center justify-center"><User className="h-5 w-5 text-[#059669]" /></div>
                    <div><p className="text-sm text-slate-600">Client</p><p className="font-medium text-slate-900">{client?.company || 'N/A'}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-slate-100 flex items-center justify-center"><User className="h-5 w-5 text-slate-500" /></div>
                    <div><p className="text-sm text-slate-600">Creator</p><p className="font-medium text-slate-900" data-testid="task-creator">{allUsers.find(u => u.id === task.created_by)?.full_name || 'N/A'}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-[#D97706]/10 flex items-center justify-center"><UserCheck className="h-5 w-5 text-[#D97706]" /></div>
                    <div><p className="text-sm text-slate-600">Executor</p><p className="font-medium text-slate-900" data-testid="task-executor">{assignee?.full_name || 'N/A'}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-[#7C3AED]/10 flex items-center justify-center"><Eye className="h-5 w-5 text-[#7C3AED]" /></div>
                    <div><p className="text-sm text-slate-600">Reviewer</p><p className="font-medium text-slate-900" data-testid="task-reviewer">{task.reviewer_id ? (allUsers.find(u => u.id === task.reviewer_id)?.full_name || 'N/A') : 'Not Assigned'}</p></div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-md bg-[#DC2626]/10 flex items-center justify-center"><Calendar className="h-5 w-5 text-[#DC2626]" /></div>
                    <div><p className="text-sm text-slate-600">Deadline</p><p className="font-medium text-slate-900">{new Date(task.deadline).toLocaleString()}</p></div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Comments Section */}
            <Card className="border-slate-200 shadow-sm" data-testid="task-comments-card">
              <CardHeader>
                <CardTitle className="text-xl font-semibold flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-[#5B6FD6]" />
                  Comments & Notes
                  <Badge variant="outline" className="ml-2">{comments.length}</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Add Comment Form */}
                <form onSubmit={handleAddComment} className="flex gap-2 mb-6" data-testid="add-comment-form">
                  <div className="flex-1 relative">
                    <Textarea
                      data-testid="comment-input"
                      placeholder="Add a comment or note..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      rows={2}
                      className="pr-12 resize-none"
                    />
                    <Button
                      type="submit"
                      size="sm"
                      disabled={!newComment.trim() || submittingComment}
                      className="absolute right-2 bottom-2 h-8 w-8 p-0 bg-[#5B6FD6] hover:bg-[#4a5bc5]"
                      data-testid="submit-comment-button"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </form>

                {/* Comments List */}
                {comments.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No comments yet. Be the first to add a note.</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {comments.map(comment => (
                      <div key={comment.id} className="flex gap-3" data-testid={`comment-${comment.id}`}>
                        <div className="flex-shrink-0 h-8 w-8 rounded-full bg-gradient-to-br from-[#5B6FD6] to-[#10B981] flex items-center justify-center text-white text-xs font-bold">
                          {comment.user_name?.charAt(0)?.toUpperCase() || '?'}
                        </div>
                        <div className="flex-1 bg-slate-50 rounded-lg px-4 py-3 border border-slate-100">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="font-semibold text-sm text-slate-900">{comment.user_name}</span>
                            <span className="text-xs text-slate-400">{formatCommentDate(comment.created_at)}</span>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">{comment.text}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Time Logs */}
            <Card className="border-slate-200 shadow-sm" data-testid="time-logs-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-xl font-semibold">Time Logs</CardTitle>
                  <Dialog open={isTimeLogDialogOpen} onOpenChange={setIsTimeLogDialogOpen}>
                    <DialogTrigger asChild>
                      <Button className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="add-time-log-button">
                        <Clock className="h-4 w-4 mr-2" /> Log Time
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="time-log-dialog">
                      <DialogHeader><DialogTitle>Add Time Log</DialogTitle></DialogHeader>
                      <form onSubmit={handleAddTimeLog} className="space-y-4">
                        <div>
                          <Label htmlFor="hours">Hours</Label>
                          <Input id="hours" data-testid="time-log-hours-input" type="number" step="0.5" placeholder="e.g., 2.5"
                            value={timeLogData.hours} onChange={(e) => setTimeLogData({ ...timeLogData, hours: e.target.value })} required />
                        </div>
                        <div>
                          <Label htmlFor="log-description">Description</Label>
                          <Textarea id="log-description" data-testid="time-log-description-input" placeholder="What did you work on?"
                            value={timeLogData.description} onChange={(e) => setTimeLogData({ ...timeLogData, description: e.target.value })} rows={3} required />
                        </div>
                        <div className="flex justify-end gap-3">
                          <Button type="button" variant="outline" onClick={() => setIsTimeLogDialogOpen(false)}>Cancel</Button>
                          <Button type="submit" className="bg-[#4338CA] hover:bg-[#3730A3]" data-testid="submit-time-log-button">Add Log</Button>
                        </div>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                {task.time_logs && task.time_logs.length > 0 ? (
                  <div className="space-y-3">
                    {task.time_logs.map((log) => {
                      const key = log.id || log.logged_at || log.created_at;
                      return (
                      <div key={key} className="p-4 bg-slate-50 rounded-md border border-slate-200" data-testid={`time-log-${key}`}>
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-semibold text-[#4338CA]">{log.hours} hours</span>
                          <span className="text-sm text-slate-600">{new Date(log.logged_at).toLocaleDateString()}</span>
                        </div>
                        <p className="text-sm text-slate-700">{log.description}</p>
                      </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No time logs yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            <Card className="border-slate-200 shadow-sm" data-testid="task-summary-card">
              <CardHeader><CardTitle className="text-xl font-semibold">Summary</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="text-sm text-slate-600 mb-1">Total Hours Logged</p>
                  <p className="text-3xl font-bold text-[#4338CA]">{totalHours.toFixed(1)}h</p>
                </div>
                {/* Estimate / Variance block */}
                <div className="pt-4 border-t border-slate-200" data-testid="estimate-block">
                  <div className="flex items-center justify-between mb-1">
                    <p className="text-sm text-slate-600">Estimate</p>
                    {/* Inline estimate editor — available to assignee, admin, SA, or creator. */}
                    {(currentUser?.role !== 'member'
                      || task.assignee_id === currentUser?.id
                      || task.created_by === currentUser?.id) && !isEditMode && (
                      <EstimateInlineEditor task={task} onSaved={fetchTask} />
                    )}
                  </div>
                  {task.estimate_hours ? (() => {
                    const est = parseFloat(task.estimate_hours) || 0;
                    const variance = totalHours - est;
                    const pct = est > 0 ? Math.round((variance / est) * 100) : 0;
                    const overBy = variance > 0;
                    const color = !est ? 'text-slate-400'
                      : variance <= 0 ? 'text-emerald-600'
                      : pct <= 25 ? 'text-amber-600'
                      : 'text-red-600';
                    return (
                      <>
                        <p className="text-xl font-bold text-slate-800">{est.toFixed(2)}h</p>
                        <div className="mt-2 flex items-center justify-between text-xs">
                          <span className="text-slate-500">Variance</span>
                          <span className={`font-semibold ${color}`} data-testid="task-variance">
                            {overBy ? '+' : ''}{variance.toFixed(2)}h ({pct > 0 ? '+' : ''}{pct}%)
                          </span>
                        </div>
                        {/* Progress bar against estimate */}
                        <div className="mt-2 h-2 bg-slate-100 rounded-full overflow-hidden">
                          <div
                            className={`h-full ${variance <= 0 ? 'bg-emerald-500' : pct <= 25 ? 'bg-amber-500' : 'bg-red-500'}`}
                            style={{ width: `${Math.min(100, est > 0 ? (totalHours / est) * 100 : 0)}%` }}
                          />
                        </div>
                      </>
                    );
                  })() : (
                    <p className="text-xs text-slate-400 italic">No estimate set yet. Use the pencil icon to add one.</p>
                  )}
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-1">Comments</p>
                  <p className="text-xl font-bold text-[#5B6FD6]">{comments.length}</p>
                </div>
                <div className="pt-4 border-t border-slate-200">
                  <p className="text-sm text-slate-600 mb-1">Created</p>
                  <p className="text-sm font-medium text-slate-900">{new Date(task.created_at).toLocaleDateString()}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-600 mb-1">Last Updated</p>
                  <p className="text-sm font-medium text-slate-900">{new Date(task.updated_at).toLocaleDateString()}</p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
    </div>
  );
}


function EstimateInlineEditor({ task, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(task.estimate_hours ?? '');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const num = value === '' || value === null ? null : parseFloat(value);
      await api.put(`/tasks/${task.id}`, { estimate_hours: num });
      toast.success(num ? `Estimate set to ${num}h` : 'Estimate cleared');
      setEditing(false);
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save estimate');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <button
        type="button"
        onClick={() => { setValue(task.estimate_hours ?? ''); setEditing(true); }}
        className="text-xs text-[#5B6FD6] hover:text-[#4338CA] inline-flex items-center gap-1"
        data-testid="estimate-edit-btn"
        title={task.estimate_hours ? 'Update estimate' : 'Add estimate'}
      >
        <Pencil className="h-3 w-3" /> {task.estimate_hours ? 'Edit' : 'Add'}
      </button>
    );
  }

  return (
    <div className="flex items-center gap-1">
      <Input
        type="number" min="0" step="0.25" autoFocus
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') save(); if (e.key === 'Escape') setEditing(false); }}
        className="h-7 w-20 text-xs"
        data-testid="estimate-inline-input"
      />
      <button
        type="button"
        onClick={save}
        disabled={saving}
        className="h-7 w-7 inline-flex items-center justify-center rounded bg-emerald-600 hover:bg-emerald-700 text-white"
        data-testid="estimate-save-btn"
        title="Save"
      >
        <Check className="h-3 w-3" />
      </button>
    </div>
  );
}
